import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Project } from '@types'

import caseIcon from '@assets/images/case-icon.svg'
import moreIcon from '@assets/images/three-dot-icon.svg'
import trashIcon from '@assets/images/trash-icon.svg'

import { useProjects } from '@hooks/project/projects.hook'
import { useDeleteProject } from '@hooks/project/delete-project.hook'

import { Refresh } from '@components/Refresh'
import { DropDownAction, Action } from '@components/DropDownAction'
import { PopUpDelete } from '@components/PopUpDelete'

import {
  Container,
  SectionContainer,
  SectionTitle,
  RowContainer,
  RowIconContainer,
  RowIcon,
  RowLabel,
  MoreButton,
  MoreIconImg,
  DeleteIconImg,
} from './ProjectList.styled'

export const ProjectList = (): JSX.Element => {
  const { data, loading, error, refetch } = useProjects()

  const isLoading = loading ? 1 : error ? -1 : 0

  const projects = useMemo(() => data?.projects || [], [data?.projects])
  const sharedProjects = useMemo(() => data?.sharedProjects || [], [data?.sharedProjects])

  const navigate = useNavigate()

  const { projectId } = useParams()

  const { t, i18n } = useTranslation()

  const { pathname } = useLocation()

  const getDefaultProjectPath = useCallback(
    (project: Project) => {
      const path = `/${i18n.language}/projects/${project.id}`
      return path
    },
    [i18n.language],
  )

  const isActive = (project: Project) => {
    return pathname.startsWith(getDefaultProjectPath(project))
  }

  const getProjectPath = useCallback(
    (project: Project) => {
      if (pathname.startsWith(`/${i18n.language}/projects/`)) {
        const path = pathname
          .split('/')
          .reduce((acc, sub, index) => (index == 3 ? `${acc}/${project.id}` : `${acc}/${sub}`))

        return path
      }

      const path = `${getDefaultProjectPath(project)}/view`
      return path
    },
    [getDefaultProjectPath, i18n.language, pathname],
  )

  useEffect(() => {
    if (isLoading == 1) return

    const path = `/${i18n.language}/projects`

    if (pathname.startsWith(path) && !projectId && (projects.length > 0 || sharedProjects.length > 0)) {
      if (projects.length > 0) {
        navigate(getProjectPath(projects[projects.length - 1]))
      } else if (sharedProjects.length > 0) {
        navigate(getProjectPath(sharedProjects[sharedProjects.length - 1]))
      }
    } else if (pathname.startsWith(path) && projectId) {
      const projectExists = projects.findIndex((project) => project.id == projectId) != -1
      const sharedProjectExists = sharedProjects.findIndex((project) => project.id == projectId) != -1

      if (!projectExists && !sharedProjectExists) {
        if (projects.length > 0) {
          navigate(getProjectPath(projects[projects.length - 1]))
        } else if (sharedProjects.length > 0) {
          navigate(getProjectPath(sharedProjects[sharedProjects.length - 1]))
        } else {
          navigate(`/${i18n.language}/projects`)
        }
      }
    }
  }, [getProjectPath, i18n.language, isLoading, navigate, pathname, projectId, projects, sharedProjects])

  const { deleteProject } = useDeleteProject()

  const [deletingProject, setDeletingProject] = useState<Project | undefined>(undefined)

  const onDeleteproject = () => {
    if (deletingProject) {
      const index = projects.findIndex((p) => p.id == deletingProject.id)
      const nextProject = projects[index + 1]
      const prevProject = projects[index - 1]

      deleteProject(deletingProject.id)

      setDeletingProject(undefined)

      if (isActive(deletingProject)) {
        if (nextProject) {
          navigate(getProjectPath(nextProject))
        } else if (prevProject) {
          navigate(getProjectPath(prevProject))
        } else {
          navigate(`/${i18n.language}/projects`)
        }
      }
    }
  }

  return (
    <Container>
      <SectionContainer>
        <SectionTitle>{t('modules_main_components_SideBarMenu_label_my_projects')}</SectionTitle>
        <Refresh
          loading={isLoading}
          small
          onRefresh={refetch}
          empty={{ label: t('modules_main_components_SideBarMenu_label_empty_projects') }}
        >
          {projects.length > 0 &&
            projects.map((project) => (
              <ProjectRow
                key={project.id}
                project={project}
                onClickDelete={() => {
                  setDeletingProject(project)
                }}
                isActive={isActive}
                getProjectPath={getProjectPath}
              />
            ))}
        </Refresh>
      </SectionContainer>

      {sharedProjects.length > 0 && (
        <SectionContainer>
          <SectionTitle>{t('modules_main_components_SideBarMenu_label_shared_projects')}</SectionTitle>
          {sharedProjects.map((project) => (
            <ProjectRow key={project.id} project={project} isActive={isActive} getProjectPath={getProjectPath} />
          ))}
        </SectionContainer>
      )}

      {deletingProject && (
        <PopUpDelete
          title={t('modules_main_components_SideBarMenu_label_delete_project_title')}
          description={t('modules_main_components_SideBarMenu_label_delete_project_description', {
            name: deletingProject.name,
          })}
          onSubmit={onDeleteproject}
          onClose={() => setDeletingProject(undefined)}
        />
      )}
    </Container>
  )
}

interface ProjectRowProps {
  project: Project
  isActive: (project: Project) => boolean
  getProjectPath: (project: Project) => string
  onClickDelete?: () => void
}

const ProjectRow = (props: ProjectRowProps): JSX.Element => {
  const { project, isActive, getProjectPath, onClickDelete } = props

  const { t } = useTranslation()

  const [hover, setHover] = useState(false)

  const actions: Action[] = [
    {
      key: 'delete',
      label: t('modules_main_components_SideBarMenu_label_delete_project_button'),
      Icon: () => <DeleteIconImg src={trashIcon} />,
    },
  ]

  const onClickAction = (action: Action) => {
    if (action.key == 'delete') {
      setHover(false)

      onClickDelete?.()
    }
  }

  return (
    <RowContainer
      key={project.id}
      onMouseEnter={() => setHover(true)}
      onMouseMove={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      to={getProjectPath(project)}
      className={isActive(project) ? 'active' : undefined}
    >
      <RowIconContainer>
        <RowIcon src={caseIcon} />
      </RowIconContainer>
      <RowLabel>{project.name}</RowLabel>

      {onClickDelete && hover && (
        <DropDownAction
          mask="light"
          sections={[{ key: '', actions }]}
          onClickAction={onClickAction}
          WrappedComponent={() => (
            <MoreButton>
              <MoreIconImg src={moreIcon} />
            </MoreButton>
          )}
        />
      )}
    </RowContainer>
  )
}
